import React from "react";

import makeStyles from "@mui/styles/makeStyles";

import ROUTES from "../../../config/routes.json";
import { useAuthState } from "../../../store/auth";
import { preload } from "../Router";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100vw",
    height: "100%",
  },
  logo: {
    display: "flex",
    border: "0 solid",
    borderRadius: "100%",
    padding: theme.spacing(4),
    boxShadow: "0 0.5rem 2rem rgba(0, 0, 0, 0.25)",
    animation: `$pulse 2000ms infinite ${theme.transitions.easing.easeInOut}`,
    maxWidth: 100,
    maxHeight: 100,
    width: "15%",
    backgroundColor: theme.palette.primary.main,
    boxSizing: "content-box",
    "& img": {
      filter: "invert(1)", // Black to white
      width: "100%",
      height: "auto",
    },
  },
  "@keyframes pulse": {
    "0%": {
      transform: "scale(100%)",
    },
    "50%": {
      transform: "scale(110%)",
    },
    "100%": {
      transform: "scale(100%)",
    },
  },
}));

export default function SplashScreen({ children }) {
  const classes = useStyles();
  const [pagesLoaded, setPagesLoaded] = React.useState(false);
  const { hasLoaded: authLoaded } = useAuthState();

  React.useEffect(() => {
    if (!pagesLoaded) {
      preload(ROUTES).then(() => setPagesLoaded(true));
    }
  }, [pagesLoaded, setPagesLoaded]);

  if (authLoaded && pagesLoaded) {
    return children;
  }

  return (
    <div className={classes.root}>
      <div className={classes.logo}>
        {/* Use a data URI to guarantee image loads simultaneously with the page */}
        <img
          src="data:image/png;base64,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"
          alt="maeva logo"
          width="196"
          height="196"
        />
      </div>
    </div>
  );
}
